import { useEffect, useState } from "react";
import { Coins, Sparkles, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loading } from "../components/Layout";

export default function Wallet() {
  const { coins, currency } = useAuth();
  const [packages, setPackages] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [billing, setBilling] = useState({
    phone: "",
    address1: "",
    city: "",
    state: "",
    zipcode: "",
  });

  useEffect(() => {
    api.get("/packages", { params: { currency } }).then((r) => setPackages(r.data));
    api.get("/wallet/ledger").then((r) => setLedger(r.data)).catch(() => {});
  }, [currency]);

  const openCheckout = (pkg) => {
    if (currency !== "INR") {
      toast.error("PayU international checkout will unlock after merchant approval.");
      return;
    }
    setSelectedPackage(pkg);
  };

  const buy = async (event) => {
    event.preventDefault();
    if (!selectedPackage) {
      return;
    }
    setBusy(selectedPackage.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: selectedPackage.id,
        currency,
        ...billing,
      });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value ?? "");
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      toast.error(apiError(e));
      setBusy("");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 animate-fade-up">
      <div className="glass border rounded-xl p-8 mb-10 flex items-center justify-between">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground mb-2">Your balance</p>
          <div className="flex items-center gap-3">
            <Coins size={32} className="text-gold" strokeWidth={1.5} />
            <span className="font-display text-5xl text-gold tabular-nums" data-testid="wallet-coin-balance">{coins}</span>
            <span className="text-muted-foreground">coins</span>
          </div>
        </div>
        <Sparkles className="text-gold/40 hidden sm:block" size={40} strokeWidth={1} />
      </div>

      <h2 className="font-display text-2xl mb-5">Buy coins</h2>
      <p className="mb-5 text-sm text-muted-foreground" data-testid="payu-checkout-note">
        {currency === "INR"
          ? "Continue to secure PayU checkout to complete your coin purchase."
          : "PayU checkout is currently available in INR while international approval is pending."}
      </p>
      {packages === null ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="packages-grid">
          {packages.map((p) => (
            <div key={p.id} data-testid={`package-${p.id}`} className="border border-white/10 rounded-xl p-6 bg-card/50 hover:border-gold/40 transition-colors duration-300 flex flex-col">
              <p className="uppercase tracking-[0.15em] text-xs text-muted-foreground">{p.name}</p>
              <div className="flex items-baseline gap-2 mt-3">
                <Coins size={20} className="text-gold" strokeWidth={1.5} />
                <span className="font-display text-3xl text-gold tabular-nums">{p.coins}</span>
              </div>
              <p className="text-2xl font-semibold mt-2">{p.symbol}{p.price_local}</p>
              <p className="text-xs text-muted-foreground">≈ ${p.price_usd} USD</p>
              <Button
                data-testid={`buy-${p.id}`}
                onClick={() => openCheckout(p)}
                disabled={busy === p.id || currency !== "INR"}
                className="mt-5 bg-primary hover:bg-primary/90 transition-colors duration-300"
              >
                {busy === p.id ? "Opening PayU..." : "Buy now"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={Boolean(selectedPackage)} onOpenChange={(open) => !open && setSelectedPackage(null)}>
        <DialogContent data-testid="payu-billing-modal">
          <DialogHeader>
            <DialogTitle>PayU checkout details</DialogTitle>
            <DialogDescription>
              PayU requires a contact number to start your secure INR payment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={buy} className="grid gap-4" data-testid="payu-billing-form">
            <div className="grid gap-2">
              <Label htmlFor="payu-phone">Phone number</Label>
              <Input
                id="payu-phone"
                data-testid="payu-phone-input"
                value={billing.phone}
                onChange={(event) => setBilling({ ...billing, phone: event.target.value })}
                placeholder="9876543210"
                inputMode="tel"
                minLength={6}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payu-address">Billing address (optional)</Label>
              <Input
                id="payu-address"
                data-testid="payu-address-input"
                value={billing.address1}
                onChange={(event) => setBilling({ ...billing, address1: event.target.value })}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="payu-city">City</Label>
                <Input
                  id="payu-city"
                  data-testid="payu-city-input"
                  value={billing.city}
                  onChange={(event) => setBilling({ ...billing, city: event.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="payu-state">State</Label>
                <Input
                  id="payu-state"
                  data-testid="payu-state-input"
                  value={billing.state}
                  onChange={(event) => setBilling({ ...billing, state: event.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payu-zipcode">Postal code</Label>
              <Input
                id="payu-zipcode"
                data-testid="payu-zipcode-input"
                value={billing.zipcode}
                onChange={(event) => setBilling({ ...billing, zipcode: event.target.value })}
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="payu-billing-submit"
                disabled={busy === selectedPackage?.id}
                className="bg-primary hover:bg-primary/90"
              >
                {busy === selectedPackage?.id ? "Opening PayU..." : "Continue to PayU"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <h2 className="font-display text-2xl mb-5">Transaction history</h2>
      <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="ledger-list">
        {ledger.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">No transactions yet.</p>
        ) : (
          ledger.map((l) => (
            <div key={l.id} className="flex items-center justify-between px-6 py-4 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3">
                {l.amount >= 0 ? <ArrowUpRight size={16} className="text-emerald-400" /> : <ArrowDownRight size={16} className="text-primary" />}
                <div>
                  <p className="text-sm capitalize">{l.type}</p>
                  <p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</p>
                </div>
              </div>
              <span className={`tabular-nums font-medium ${l.amount >= 0 ? "text-emerald-400" : "text-primary"}`}>
                {l.amount >= 0 ? "+" : ""}{l.amount}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
